import literals from '@/utils/literals';

/**
 * This function is used to mark the balls that have been played in a game.
 * It does this by adding a specific class to the HTML elements that represent these balls.
 *
 * @param {Object} model - The model object that contains the sequence of activities.
 * @param {string} elementId - The id of the HTML element that contains the balls.
 *
 * @example
 * markPlayedBalls(model, 'elementId');
 */
export const markPlayedBalls = (model, elementId) => {
  // Find the cells of the bouliers
  const element = document.getElementById(elementId) || document;
  const boulierCells = element.getElementsByClassName(literals.numberCells);

  // Remove the 'bg-cyan' class from all boulier cells
  for (const cell of boulierCells) {
    cell.classList.remove('bg-cyan');
  }

  // Get the ids of the balls that have been played
  const boulesTirees =
    model?.sequenceActivities?.map((boule) => boule.ballId) || [];

  // For each played ball, find its corresponding cell and add the 'bg-cyan' class
  for (const boule of boulesTirees) {
    const bouleCells = element.getElementsByClassName(`number-${boule}`);
    for (const cell of bouleCells) {
      cell.classList.add('bg-cyan');
    }
  }
};

/**
 * Determines the color of a sheet based on the sequence and draw.
 *
 * @param {number} sequence - The sequence number.
 * @param {Object} tirage - The draw object.
 * @param {Array} tirage.tours - The array of tours.
 * @param {number} tirage.nombreTour - The number of tours.
 * @returns {string} The color of the sheet.
 */
export function feuilletCouleur(sequence, tirage) {
  // Check if any tour has the same sequence and is half-half
  var isMoitieMoitie = tirage.tours.some(function (t) {
    return t.sequence === sequence && t.moitieMoitie;
  });

  // If half-half, return color-5
  if (isMoitieMoitie) return 'color-5';

  // If the number of tours is less than 6, return yellow
  if (tirage.nombreTour < 6) return 'yellow';

  // Determine the color based on the sequence
  switch (sequence) {
    case 1:
      return 'color-1';
    case 2:
      return 'color-3';
    case 3:
      return 'color-4';
    case 4:
      return 'yellow';
    case 5:
      return 'color-5';
    case 6:
      return 'color-2-light';
    default:
      return 'white';
  }
}

/**
 * This function is used to build a table for a draw (tirage) in a game.
 * It does this by iterating over the number of rounds in the draw and creating an object for each round.
 * Each round object contains the round number, an array of games, and a color determined by the setBackColor function.
 * For each round, it iterates over the tours in the draw. If the sequence of the tour matches the round number,
 * it adds additional properties to the tour (name and cartePleineMessage) and pushes the tour to the games array of the round.
 * Finally, it pushes the round object to the tirageTable array.
 *
 * @param {Object} tirage - The draw object that contains the number of rounds and an array of tours.
 * @param {number} tirage.nombreTour - The number of rounds in the draw.
 * @param {Array} tirage.tours - The array of tours in the draw.
 * @param {Object} model - The model object that contains the types of draws.
 * @param {Array} model.typeTirages - The array of types of draws.
 *
 * @returns {Array} The tirageTable array that contains an object for each round in the draw.
 *
 * @example
 * buildTirageTable(tirage, model);
 */
export const buildTirageTable = (tirage, model, messages) => {
  const tirageTable = [];
  for (let i = 1; i <= tirage?.nombreTour; i++) {
    const calulatedTour = {
      number: i,
      games: [],
      color: setBackColor(i, tirage),
    };
    for (const tour of tirage.tours) {
      if (tour.sequence === i) {
        // Vérifier que typeTirages est un tableau avant d'utiliser find
        if (Array.isArray(model.typeTirages)) {
          const typeTirage = model.typeTirages.find(
            (type) => type.id === tour.typeTirageId
          );
          if (typeTirage) {
            tour.name = typeTirage.name;
          }
        }
        tour.cartePleineMessage = calculateCartPleinneMessage(
          tour.typeTirageId,
          model,
          messages
        );
        tour.prix = tour.moitieMoitie
          ? tirage.prixMoitieMoitie
            ? tirage.prixMoitieMoitie
            : null
          : tour.prix;
        calulatedTour.games.push(tour);
      }
    }
    tirageTable.push(calulatedTour);
  }
  return tirageTable;
};

/**
 * This function calculates the full card message based on the type of draw.
 * It first finds the draw type in the tirageTypes array using the provided typeTirageId.
 * Then, it finds the corresponding message in the model's messages array using the cartePleinneMessageId from the found tirageType.
 * Finally, it returns the description of the found message or null if no message was found.
 *
 * @param {number} typeTirageId - The ID of the type of draw.
 * @param {object} model - The model object containing an array of messages.
 * @returns {string|null} The description of the found message or null if no message was found.
 */

function calculateCartPleinneMessage(typeTirageId, model, messages) {
  const tirageTypes = [
    {
      typeTirageId: 14,
      cartePleinneMessageId: 12,
    },
    {
      typeTirageId: 16,
      cartePleinneMessageId: 13,
    },
    {
      typeTirageId: 17,
      cartePleinneMessageId: 15,
    },
  ];
  // find tirageType by typeTirageId in tirageTypes
  const tirageType = tirageTypes.find((t) => t.typeTirageId === typeTirageId);
  // find message in messages where id === itragetype.cartePleinneMessageId
  const message = messages?.find(
    (m) => m.id === tirageType?.cartePleinneMessageId
  );
  // return message.description or null
  return message?.description;
}

function setBackColor(sequence, tirage) {
  if (sequence === tirage.sequenceActif && tirage.isJeuxActif) {
    return '#4287f5';
  }
  if (!tirage.isJeuxActif) {
    return feuilletCouleur(sequence, tirage);
  }
  return 'white';
}

export const findActifTour = (tirage, model) => {
  // Vérifier que tirage est défini et est un tableau avant d'utiliser .find()
  if (!tirage || !Array.isArray(tirage)) {
    return undefined;
  }

  const actifTiour = tirage.find(
    (t) => t.number === model?.tirage?.sequenceActif
  );
  return actifTiour;
};

/**
 * This function checks how many lines are formed by the played balls on the card.
 * A line is formed if all cells in the line are included in the played balls.
 * The function returns the number of lines formed.
 *
 * @param {Array} playedBallsOnCard - An array of objects representing the played balls on the card.
 * Each object has a 'cell' property representing the cell id and a 'number' property representing the ball number.
 * @returns {number} The number of lines formed by the played balls on the card.
 */
const hasLines = (playedBallsOnCard) => {
  let numberOfLines = 0;
  const lines = [
    //horizontal lines
    ['b1', 'i1', 'n1', 'g1', 'o1'],
    ['b2', 'i2', 'n2', 'g2', 'o2'],
    ['b3', 'i3', 'n3', 'g3', 'o3'],
    ['b4', 'i4', 'n4', 'g4', 'o4'],
    ['b5', 'i5', 'n5', 'g5', 'o5'],
    //vertical lines
    ['b1', 'b2', 'b3', 'b4', 'b5'],
    ['i1', 'i2', 'i3', 'i4', 'i5'],
    ['n1', 'n2', 'n3', 'n4', 'n5'],
    ['g1', 'g2', 'g3', 'g4', 'g5'],
    ['o1', 'o2', 'o3', 'o4', 'o5'],
    //diagonals
    ['b1', 'i2', 'n3', 'g4', 'o5'],
    ['b5', 'i4', 'n3', 'g2', 'o1'],
  ];
  for (const line of lines) {
    // if playedBallsOnCard contains all the cells in the line add 1 to the number of lines
    if (
      line.every((cell) =>
        playedBallsOnCard.some(
          (ball) => ball.cell.toLowerCase() === cell.toLowerCase()
        )
      )
    ) {
      numberOfLines++;
    }
  }
  return numberOfLines;
};

/**
 * This function adds configuration to each game in a tour.
 * The configuration is a binary string where each character represents a cell on the card.
 * A '1' means the cell is included in the game, and a '0' means it is not.
 *
 * @param {Object} tour - The tour object that contains an array of games.
 * @param {Object} model - The model object that contains an array of draw types.
 */
// Cache pour les configurations de jeux (optimisation)
const configurationCache = new Map();

const addConfigrationToTourGames = (tour, model) => {
  // Vérification défensive: si tour est undefined ou n'a pas de games, retourner
  if (!tour || !tour.games || !Array.isArray(tour.games)) {
    return;
  }

  const games = tour.games;

  for (const game of games) {
    // Si la configuration existe déjà, ne pas la recalculer (optimisation)
    if (game.configuration) {
      continue;
    }

    // Vérifier que typeTirages est un tableau avant d'utiliser find
    if (Array.isArray(model.typeTirages)) {
      const tirageType = model.typeTirages.find(
        (type) => type.id === game.typeTirageId
      );
      if (tirageType && tirageType.configuration) {
        // Vérifier le cache avant de calculer
        const cacheKey = `${game.typeTirageId}-${tirageType.configuration}`;
        let configuration = configurationCache.get(cacheKey);

        if (!configuration) {
          // tirageTypeConfiguration is a 25char binarie
          const tirageTypeConfiguration = tirageType.configuration;
          configuration = {
            b1: tirageTypeConfiguration[0],
            b2: tirageTypeConfiguration[1],
            b3: tirageTypeConfiguration[2],
            b4: tirageTypeConfiguration[3],
            b5: tirageTypeConfiguration[4],
            i1: tirageTypeConfiguration[5],
            i2: tirageTypeConfiguration[6],
            i3: tirageTypeConfiguration[7],
            i4: tirageTypeConfiguration[8],
            i5: tirageTypeConfiguration[9],
            n1: tirageTypeConfiguration[10],
            n2: tirageTypeConfiguration[11],
            n3: '1', //tirageTypeConfiguration[12],
            n4: tirageTypeConfiguration[13],
            n5: tirageTypeConfiguration[14],
            g1: tirageTypeConfiguration[15],
            g2: tirageTypeConfiguration[16],
            g3: tirageTypeConfiguration[17],
            g4: tirageTypeConfiguration[18],
            g5: tirageTypeConfiguration[19],
            o1: tirageTypeConfiguration[20],
            o2: tirageTypeConfiguration[21],
            o3: tirageTypeConfiguration[22],
            o4: tirageTypeConfiguration[23],
            o5: tirageTypeConfiguration[24],
          };
          // Mettre en cache (limiter à 100 entrées pour éviter les fuites mémoire)
          if (configurationCache.size > 100) {
            const firstKey = configurationCache.keys().next().value;
            configurationCache.delete(firstKey);
          }
          configurationCache.set(cacheKey, configuration);
        }

        game.configuration = configuration;
      }
    }
  }
};

/**
 * This function determines which games the card has won in the active tour.
 * It checks for each game if all cells included in the game are in the played balls on the card.
 * If a game is won, its name is added to the gamesWonONCard array.
 * The function also checks if the card has won the 2-lines or 3-lines game.
 * The function finally sets the gamesWonONCard array in the state.
 *
 * @param {Object} card - The card object that contains the ball numbers for each cell.
 * @param {Object} model - The model object that contains the sequence of activities and the types of draws.
 * @param {Object} actifTour - The active tour object that contains an array of games.
 */
export const whatGameHasCardwon = (card, model, actifTour, playedBallsSet = null) => {
  // Vérification défensive: si actifTour est undefined ou n'a pas de games, retourner un tableau vide
  if (!model || !actifTour || !actifTour.games || !Array.isArray(actifTour.games)) {
    return [];
  }

  const games = actifTour.games;
  const _2linesGame = 12;
  const _3lineGame = 13;
  const cardWithFree = {
    ...card,
    n3: 'Free',
  };

  // Optimisation: utiliser playedBallsSet si fourni, sinon calculer (rétrocompatibilité)
  let playedBalls;
  if (playedBallsSet instanceof Set) {
    playedBalls = playedBallsSet;
  } else {
    // Fallback pour rétrocompatibilité
    playedBalls = [];
    for (const sequence of model.sequenceActivities) {
      if (sequence.isCanceled) continue;
      playedBalls.push(sequence.ballId);
    }
  }

  const playedBallsOnCard = [
    {
      cell: 'n3',
      number: 'Free',
    },
  ];
  for (const cell in cardWithFree) {
    // Optimisation: utiliser Set.has() si Set fourni, sinon Array.includes() (rétrocompatibilité)
    const isPlayed = playedBalls instanceof Set
      ? playedBalls.has(cardWithFree[cell])
      : playedBalls.includes(cardWithFree[cell]);

    if (isPlayed) {
      const playedBall = {
        cell: cell,
        number: cardWithFree[cell],
      };
      playedBallsOnCard.push(playedBall);
    }
  }
  addConfigrationToTourGames(actifTour, model);

  const gamesWonONCard = [];

  const numberOfLines = hasLines(playedBallsOnCard);
  if (numberOfLines > 1) {
    if (
      games.some(
        (game) => !game.gagnantActif && game.typeTirageId === _3lineGame
      ) &&
      numberOfLines >= 3
    ) {
      gamesWonONCard.push(
        games.find((game) => game.typeTirageId === _3lineGame).name
      );
    }
    if (
      games.some(
        (game) => !game.gagnantActif && game.typeTirageId === _2linesGame
      ) &&
      numberOfLines >= 2
    ) {
      gamesWonONCard.push(
        games.find((game) => game.typeTirageId === _2linesGame).name
      );
    }
  }

  // letters
  for (const game of actifTour.games) {
    // skip 2lines and 3lines
    if (game.typeTirageId === _2linesGame || game.typeTirageId === _3lineGame)
      continue;

    const winningCells = [];
    for (const cell in game.configuration) {
      if (game.configuration[cell] == 1) {
        winningCells.push(cell);
      }
    }

    // if all WinningCells are in playedBallsOnCard then add game.name to gamesWonONCard
    const gameWon = winningCells.every((cell) =>
      playedBallsOnCard.some(
        (ball) => ball.cell.toLowerCase() === cell.toLowerCase()
      )
    );
    if (gameWon) {
      if (!game.gagnantActif) gamesWonONCard.push(game.name);
    }
  }
  return gamesWonONCard;
};
